import { UserRole } from '@avanzu/types';
import {
  BarChartTwoTone,
  BusinessTwoTone,
  ChevronLeft,
  ChevronRight,
  HandshakeTwoTone,
  HistoryTwoTone,
  LocalOfferTwoTone,
  PeopleTwoTone,
  SettingsTwoTone,
} from '@mui/icons-material';
import {
  AppBar,
  Badge,
  Box,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import AvanzuHeader from '../components/AvanzuAdminHeader';
import { ProfileMenu } from '../components/ProfileMenu';
import UserAvatar from '../components/UserAvatar';
import { useAuth } from '../contexts/AuthContext';
import VersionBadge from '../components/VersionBadge';

const drawerWidth = 240;
const drawerWidthCollapsed = 64;

interface NavItem {
  text: string;
  path: string;
  icon: React.ComponentType;
}

interface NavSection {
  items: NavItem[];
}

const getNavSections = (): NavSection[] => {
  return [
    {
      items: [
        { text: 'Dashboard', path: '/app/', icon: BarChartTwoTone },
        { text: 'Activities', path: '/app/activities', icon: HistoryTwoTone },
        { text: 'Users', path: '/app/users', icon: PeopleTwoTone },
        { text: 'Organisations', path: '/app/organisations', icon: BusinessTwoTone },
        { text: 'Coupons', path: '/app/coupons', icon: LocalOfferTwoTone },
        { text: 'Affiliates', path: '/app/affiliates', icon: HandshakeTwoTone },
      ],
    },
    {
      items: [{ text: 'Settings', path: '/settings', icon: SettingsTwoTone }],
    },
  ];
};

export const AppWrapper: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(true);

  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  }, [isMobile]);

  const navSections = getNavSections();

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const isActive = (path: string) => {
    if (path === '/app/' && location.pathname === '/app/') return true;
    return path !== '/app/' && location.pathname.startsWith(path);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <CssBaseline />
      <AppBar position="static">
        <Toolbar sx={{ mr: 6 }}>
          <Box sx={{ ml: 2, width: '220px' }}>
            <AvanzuHeader />
          </Box>

          <Box sx={{ flexGrow: 1 }} />
          <Stack direction="row" alignItems="center" spacing={2}>
            {!isMobile && (
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Administrator
              </Typography>
            )}
            <IconButton size="large" onClick={handleMenu} color="inherit">
              <Badge badgeContent={0} color="error" invisible={true}>
                <UserAvatar
                  initials={
                    (user?.firstName?.[0] || '')?.toUpperCase() +
                    (user?.lastName?.[0] || '')?.toUpperCase()
                  }
                  avatarUrl={user?.avatarUrl || undefined}
                  size="small"
                  color="primary.main"
                />
              </Badge>
            </IconButton>
          </Stack>
          <ProfileMenu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            user={user}
            userRole={UserRole.AVANZU}
          />
        </Toolbar>
      </AppBar>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <Drawer
          sx={{
            width: sidebarOpen ? drawerWidth : drawerWidthCollapsed,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: sidebarOpen ? drawerWidth : drawerWidthCollapsed,
              boxSizing: 'border-box',
              position: 'relative',
              height: '100%',
              transition: 'width 0.3s',
              overflowX: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            },
          }}
          variant="permanent"
          anchor="left"
        >
          <Box sx={{ mt: 2, ml: 1, flex: 1, display: 'flex', flexDirection: 'column' }}>
            <List sx={{ flex: 1 }}>
              {navSections.map((section, sectionIndex) => (
                <React.Fragment key={sectionIndex}>
                  {sectionIndex > 0 && <Divider sx={{ my: 1, mx: 1 }} />}
                  {section.items.map((item) => {
                    const Icon = item.icon;
                    const active = isActive(item.path);

                    return (
                      <Tooltip
                        key={item.text}
                        title={!sidebarOpen ? item.text : ''}
                        placement="right"
                      >
                        <ListItemButton
                          selected={active}
                          onClick={() => navigate(item.path)}
                          sx={{
                            height: 36,
                            px: 0,
                            ml: 2,
                            mr: 0,
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 42,
                              justifyContent: 'center',
                              mr: sidebarOpen ? 2 : 1,
                            }}
                          >
                            <Icon />
                          </ListItemIcon>
                          {sidebarOpen && (
                            <ListItemText
                              primary={item.text}
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            />
                          )}
                        </ListItemButton>
                      </Tooltip>
                    );
                  })}
                </React.Fragment>
              ))}
            </List>

            <Box
              sx={{
                display: 'flex',
                justifyContent: sidebarOpen ? 'flex-end' : 'center',
                mb: 2,
                ml: sidebarOpen ? 0 : 2,
              }}
            >
              <IconButton onClick={toggleSidebar} size="small">
                {sidebarOpen ? <ChevronLeft /> : <ChevronRight />}
              </IconButton>
            </Box>
          </Box>
        </Drawer>
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            bgcolor: 'background.default',
            p: 3,
            pr: 10,
            overflow: 'auto',
          }}
        >
          <Outlet />
          <VersionBadge />
        </Box>
      </Box>
    </Box>
  );
};

export default AppWrapper;
